import {Grid, Box, Pillar} from '@primer/react-brand/esm'

import {getPrimerIcon} from '@github-ui/swp-core/lib/utils/icons'

import type {FlexSuiteIconCardsHeroProps} from './types'
import {renderPillarHeading, renderWithoutParagraphs} from '../../utils'
import {HeroContent} from './HeroContent'

/**
 * Icon Cards hero: center-aligned hero with a row of icon pillars below.
 * Each item renders an icon, heading, and description using the
 * `iconItem` Contentful content type. Despite the historical "Icon Cards"
 * variant name, items have no link/CTA — they're presentational pillars.
 */
export function FlexSuiteHeroIconCards({fields}: {fields: FlexSuiteIconCardsHeroProps}) {
  const {heroIconCards} = fields

  return (
    <Box data-testid="hero-icon-cards" borderBlockEndWidth="thin" borderStyle="solid" borderColor="muted">
      <HeroContent {...fields} />

      {heroIconCards && heroIconCards.length > 0 && (
        <Grid style={{paddingInline: 0}}>
          <Grid.Column>
            <Box
              borderColor="muted"
              borderStyle="solid"
              borderBlockStartWidth="thin"
              borderInlineEndWidth={{regular: 'thin'}}
              borderInlineStartWidth={{regular: 'thin'}}
            >
              <Grid
                style={{
                  '--brand-Grid-spacing-row': '0',
                  '--brand-Grid-spacing-column-gap': '0',
                }}
              >
                {heroIconCards.map((card, index) => {
                  const Octicon = card.fields.icon ? getPrimerIcon(card.fields.icon) : undefined
                  const isMiddle = index > 0 && index < heroIconCards.length - 1

                  return (
                    <Grid.Column key={card.sys.id} span={{xsmall: 12, large: 4}}>
                      <Box
                        padding={{narrow: 32, regular: 48}}
                        borderColor="muted"
                        borderStyle="solid"
                        borderBlockEndWidth={{narrow: 'thin', wide: 'none'}}
                        borderBlockStartWidth={{narrow: index > 0 ? 'thin' : 'none', wide: 'none'}}
                        borderInlineEndWidth={{wide: isMiddle ? 'thin' : 'none'}}
                        borderInlineStartWidth={{wide: isMiddle ? 'thin' : 'none'}}
                      >
                        <Pillar>
                          {Octicon && <Pillar.Icon icon={<Octicon />} />}
                          {card.fields.heading && (
                            <Pillar.Heading>{renderPillarHeading(card.fields.heading)}</Pillar.Heading>
                          )}
                          {card.fields.description && (
                            <Pillar.Description>{renderWithoutParagraphs(card.fields.description)}</Pillar.Description>
                          )}
                        </Pillar>
                      </Box>
                    </Grid.Column>
                  )
                })}
              </Grid>
            </Box>
          </Grid.Column>
        </Grid>
      )}
    </Box>
  )
}
